import { AxiosResponseHeaders } from 'axios';

// Revoking in the same task as the click can cancel the download in WebKit; FileSaver.js waits 40s for the same reason.
const REVOKE_DELAY_MS = 40_000;

// A cross-origin link only downloads when the server sends it as an attachment, since browsers ignore `download` there.
export const downloadFromUrl = (href: string, filename: string) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  // Firefox ignores clicks on anchors that aren't in the document.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// For an object URL minted for this one download; it is released once the browser has taken the file.
export const downloadObjectUrl = (url: string, filename: string) => {
  downloadFromUrl(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
};

export const downloadBlob = (blob: Blob, filename: string) => {
  downloadObjectUrl(URL.createObjectURL(blob), filename);
};

export interface NamedBlob {
  blob: Blob;
  filename?: string;
}

/** An axios transformResponse for a download the API names: callExternalApi returns only the body, so the name is folded in. */
export const blobWithFilename = (data: Blob, headers: AxiosResponseHeaders): NamedBlob => ({
  blob: data,
  filename: filenameFromContentDisposition(headers?.['content-disposition'])
});

// Prefers the RFC 5987 `filename*` form, which carries non-ASCII names the plain `filename` form can't.
export const filenameFromContentDisposition = (header?: string | null): string | undefined => {
  if (!header) return undefined;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // Malformed percent-encoding; fall through to the plain form.
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1]?.trim();
};
